import React, { useEffect, useMemo, useRef, useState } from "react";
import { Workflow, WorkflowService } from "../../../services/WorkflowService";
import { ApiKeyOrGetter } from "../../../services/GeneralService";
import WorkflowPayloadFields from "../../Studio/WorkflowPayloadFields";
import {
  parseWorkflowRequestBody,
  DEFAULT_WORKFLOW_INPUT_JSON,
  DEFAULT_WORKFLOW_MESSAGES_JSON,
  type WorkflowPayloadMode,
} from "../../Studio/workflowRequestUtils";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { CopyIcon, Loader2Icon, PlayIcon } from "lucide-react";
import { toast } from "sonner";

function formatJson(obj: unknown): string {
  try {
    const s = JSON.stringify(obj, null, 2);
    return s === undefined ? String(obj) : s;
  } catch {
    return String(obj);
  }
}

function workflowAssistantContent(data: unknown): string | null {
  if (data === null || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;
  const choices = d.choices;
  if (!Array.isArray(choices) || choices.length === 0) return null;
  const first = choices[0];
  if (first === null || typeof first !== "object") return null;
  const msg = (first as Record<string, unknown>).message;
  if (msg === null || typeof msg !== "object") return null;
  const raw = (msg as Record<string, unknown>).content;
  if (typeof raw === "string") return raw;
  if (raw !== undefined) {
    try {
      return JSON.stringify(raw, null, 2);
    } catch {
      return String(raw);
    }
  }
  return null;
}

function errorMessage(err: unknown): string {
  if (typeof err === "string") return err;
  if (err && typeof err === "object") {
    const o = err as Record<string, unknown>;
    if (typeof o.message === "string") return o.message;
    const nested = o.error;
    if (nested && typeof nested === "object" && nested !== null) {
      const m = (nested as Record<string, unknown>).message;
      if (typeof m === "string") return m;
    }
  }
  return "Request failed";
}

interface WorkflowTestRequestTabProps {
  workflow: Workflow;
  apiKey: ApiKeyOrGetter;
}

const WorkflowTestRequestTab: React.FC<WorkflowTestRequestTabProps> = ({
  workflow,
  apiKey,
}) => {
  const [payloadMode, setPayloadMode] = useState<WorkflowPayloadMode>("input");
  const [inputJson, setInputJson] = useState(DEFAULT_WORKFLOW_INPUT_JSON);
  const [messagesJson, setMessagesJson] = useState(DEFAULT_WORKFLOW_MESSAGES_JSON);
  const [forgeReuseSession, setForgeReuseSession] = useState(false);
  const [secretsJson, setSecretsJson] = useState("");
  const [metadataJson, setMetadataJson] = useState("");
  const [sessionIdOverride, setSessionIdOverride] = useState("");
  const [includeProfile, setIncludeProfile] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastResponse, setLastResponse] = useState<string | null>(null);
  const [lastSessionId, setLastSessionId] = useState<string | null>(null);
  const runAbortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const modelRef = useMemo(
    () => `workflow:${workflow.workflow_ref_name}`,
    [workflow.workflow_ref_name]
  );

  const assistantPreview = useMemo(() => {
    if (lastResponse === null) return null;
    try {
      const parsed = JSON.parse(lastResponse) as unknown;
      const assistant = workflowAssistantContent(parsed);
      if (assistant === null || assistant === "") return null;
      return assistant;
    } catch {
      return null;
    }
  }, [lastResponse]);

  const handleRun = () => {
    const parsed = parseWorkflowRequestBody(payloadMode, inputJson, messagesJson);
    if (parsed.ok === false) {
      toast.error(parsed.error);
      return;
    }

    let secrets: Record<string, unknown> | undefined;
    if (secretsJson.trim()) {
      try {
        const s = JSON.parse(secretsJson) as unknown;
        if (s === null || typeof s !== "object" || Array.isArray(s)) {
          toast.error("Secrets must be a JSON object.");
          return;
        }
        secrets = s as Record<string, unknown>;
      } catch {
        toast.error("Invalid JSON in secrets.");
        return;
      }
    }

    let metadata: Record<string, unknown> | undefined;
    if (metadataJson.trim()) {
      try {
        const m = JSON.parse(metadataJson) as unknown;
        if (m === null || typeof m !== "object" || Array.isArray(m)) {
          toast.error("Metadata must be a JSON object.");
          return;
        }
        metadata = m as Record<string, unknown>;
      } catch {
        toast.error("Invalid JSON in metadata.");
        return;
      }
    }

    const override = sessionIdOverride.trim();
    let sid: string;
    if (override) {
      sid = override;
    } else if (forgeReuseSession && lastSessionId) {
      sid = lastSessionId;
    } else {
      sid = crypto.randomUUID();
    }

    const payload: Record<string, unknown> = {
      model: modelRef,
      session_id: sid,
    };
    if (parsed.input !== undefined) payload.input = parsed.input;
    if (parsed.messages !== undefined) payload.messages = parsed.messages;
    if (secrets) payload.secrets = secrets;
    if (metadata) payload.metadata = metadata;

    runAbortRef.current?.abort();
    const ac = new AbortController();
    runAbortRef.current = ac;

    setRunning(true);
    setLastResponse(null);
    setLastSessionId(sid);

    void WorkflowService.runWorkflowCompletionAsync(
      payload,
      apiKey,
      includeProfile,
      ac.signal
    )
      .then((data: unknown) => {
        if (ac.signal.aborted || !mountedRef.current) return;
        setLastResponse(formatJson(data));
        toast.success("Workflow completed");
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted || !mountedRef.current) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        const msg = errorMessage(err);
        setLastResponse(formatJson(err));
        toast.error(msg);
      })
      .finally(() => {
        if (!ac.signal.aborted && mountedRef.current) setRunning(false);
      });
  };

  const copySession = () => {
    if (lastSessionId) {
      void navigator.clipboard.writeText(lastSessionId);
      toast.success("Session ID copied");
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Same API as the SDK and{" "}
        <code className="bg-muted px-1.5 py-0.5 rounded font-mono text-xs">curl</code>. Model:{" "}
        <code className="bg-muted px-1.5 py-0.5 rounded font-mono text-xs">{modelRef}</code>
        . Studio will offer the same JSON-style request controls for agents so testing feels
        consistent across workflows and agents.
      </p>

      <WorkflowPayloadFields
        variant="forge"
        mode={payloadMode}
        onModeChange={setPayloadMode}
        inputJson={inputJson}
        onInputJsonChange={setInputJson}
        messagesJson={messagesJson}
        onMessagesJsonChange={setMessagesJson}
        reuseSession={forgeReuseSession}
        onReuseSessionChange={setForgeReuseSession}
        showReuseSession
        disabled={running}
      />
      <p className="text-xs text-muted-foreground -mt-2">
        Optional session override below wins over reuse. Leave both unset for a new session every run.
      </p>

      <div className="space-y-2">
        <Label htmlFor="wf-test-secrets">Secrets (optional JSON object)</Label>
        <Textarea
          id="wf-test-secrets"
          className="font-mono text-sm min-h-[80px]"
          value={secretsJson}
          onChange={(e) => setSecretsJson(e.target.value)}
          placeholder="{}"
          spellCheck={false}
          disabled={running}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="wf-test-meta">Metadata (optional JSON object)</Label>
        <Textarea
          id="wf-test-meta"
          className="font-mono text-sm min-h-[80px]"
          value={metadataJson}
          onChange={(e) => setMetadataJson(e.target.value)}
          placeholder="{}"
          spellCheck={false}
          disabled={running}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="wf-test-sid">Session ID (optional override)</Label>
        <p className="text-xs text-muted-foreground">
          Fixed ID for correlating with the Sessions tab. If empty, reuse (if checked) uses the last
          run&apos;s session; otherwise a new UUID is generated.
        </p>
        <Input
          id="wf-test-sid"
          className="font-mono text-sm"
          value={sessionIdOverride}
          onChange={(e) => setSessionIdOverride(e.target.value)}
          placeholder="Auto-generated if empty"
          disabled={running}
        />
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id="wf-profile"
          checked={includeProfile}
          onCheckedChange={(c) => setIncludeProfile(c === true)}
          disabled={running}
        />
        <Label htmlFor="wf-profile" className="font-normal cursor-pointer">
          Include timing profile (X-Profile header)
        </Label>
      </div>

      <Button type="button" onClick={handleRun} disabled={running} className="gap-2">
        {running ? (
          <Loader2Icon className="h-4 w-4 animate-spin" />
        ) : (
          <PlayIcon className="h-4 w-4" />
        )}
        Run test
      </Button>

      {lastSessionId && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Session ID</span>
          <code className="bg-muted px-2 py-0.5 rounded font-mono text-xs break-all max-w-full">
            {lastSessionId}
          </code>
          <Button type="button" variant="ghost" size="sm" onClick={copySession} className="h-8 gap-1">
            <CopyIcon className="h-3 w-3" />
            Copy
          </Button>
          <span className="text-muted-foreground text-xs w-full sm:w-auto">
            Open the Sessions tab to inspect this run.
          </span>
        </div>
      )}

      {lastResponse !== null && (
        <div className="space-y-2">
          <Label>Response</Label>
          {assistantPreview !== null && (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">
                Assistant message (workflow output), from choices[0].message.content:
              </p>
              <pre className="bg-muted rounded-lg p-4 text-xs font-mono overflow-auto max-h-[240px] whitespace-pre-wrap break-words border border-border">
                {assistantPreview}
              </pre>
            </div>
          )}
          <p className="text-xs text-muted-foreground">Full API JSON</p>
          <pre className="bg-muted rounded-lg p-4 text-xs font-mono overflow-auto whitespace-pre-wrap break-words border border-border">
            {lastResponse}
          </pre>
        </div>
      )}
    </div>
  );
};

export default WorkflowTestRequestTab;
